/**
 * B76C — USER-KEYED CLIENT FLAGS for the two once-only crossing surfaces:
 * the dual arrival banner (2e) and the coach acknowledgment (3b).
 *
 * ── WHY THIS SHAPE ────────────────────────────────────────────────────────
 * This codebase has one rule about client-side state and it is not negotiable:
 * **one account's state can never reach another account.** It is written three
 * times already — `onboardingReducer`'s stamped wizard blob (B26/F-113),
 * `forkStorage`'s stamped fork choice (F-201), and `stampedSurfaceState`'s
 * stamped draft chip (B46, the founder-reported leak where a brand-new account
 * saw the PRIOR user's "Week 9 is in."). Each of those was written because the
 * un-keyed version leaked.
 *
 * So these flags are keyed by the owning auth user id, and a read for an
 * unknown or absent id is FALSE — never "probably fine".
 *
 * ── WHY A LEDGER AND NOT A STAMPED SINGLETON ──────────────────────────────
 * `forkStorage` keeps ONE blob plus an owner stamp, and a foreign stamp reads
 * as absent. That is right for an in-progress choice: only one account can be
 * mid-onboarding at a browser, and re-asking a returning account is harmless.
 *
 * It is WRONG here, and the difference is what "once" means. With a stamped
 * singleton, account A dismissing the banner and then account B signing in
 * would overwrite A's record, and A would be shown the once-only banner a
 * SECOND time on its next visit. "Once per account" therefore needs a record
 * per account, so the store is a small id → true map.
 *
 * Bounded: `MAX_IDS` most-recent ids, oldest evicted. A shared device with
 * more than that many accounts loses the oldest record, which degrades to
 * showing a one-time banner one extra time — the harmless direction. The
 * fail-safe direction for a leak flag is never "assume seen".
 *
 * Best-effort throughout, like every other storage helper here: private-mode
 * Safari and a full quota both degrade to "not seen", never to a throw.
 */

/** 2e — the arrival banner is PENDING for this account (armed at the end of
 *  onboarding-lite, cleared when the banner is dismissed). */
export const DUAL_ARRIVAL_PENDING_KEY = 'wai:b76:dual-arrival-pending:v1';

/** 3b — the coach acknowledgment has already been shown to this account. */
export const COACH_ACK_SEEN_KEY = 'wai:b76:coach-ack-seen:v1';

/** Ids retained per key. See "Bounded" in the header. */
export const MAX_IDS = 20;

type Ledger = { ids: string[] };

function readLedger(key: string): Ledger {
  if (typeof window === 'undefined') return { ids: [] };
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return { ids: [] };
  }
  if (!raw) return { ids: [] };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return { ids: [] };
    const ids = (parsed as Partial<Ledger>).ids;
    if (!Array.isArray(ids)) return { ids: [] };
    return { ids: ids.filter((v): v is string => typeof v === 'string' && v !== '') };
  } catch {
    // Unparseable (a legacy or corrupt value) → no record, so the surface
    // renders. See the fail-safe note in the header.
    return { ids: [] };
  }
}

function writeLedger(key: string, ledger: Ledger): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(ledger));
  } catch {
    /* best-effort — see the file header. */
  }
}

/** Is this flag set for this account? False for a missing/unknown id. */
export function readUserFlag(key: string, userId: string | null | undefined): boolean {
  if (!userId) return false;
  return readLedger(key).ids.includes(userId);
}

/**
 * Set or clear this flag for this account. No-op without a user id — an
 * unattributable write is exactly what the ownership rule exists to prevent.
 */
export function writeUserFlag(
  key: string,
  userId: string | null | undefined,
  value: boolean,
): void {
  if (!userId) return;
  const { ids } = readLedger(key);
  const without = ids.filter((id) => id !== userId);
  if (!value) {
    if (without.length === ids.length) return;
    writeLedger(key, { ids: without });
    return;
  }
  // Most recent last; evict from the front when over the cap.
  const next = [...without, userId];
  writeLedger(key, { ids: next.slice(Math.max(0, next.length - MAX_IDS)) });
}

const LEDGER_KEY_PREFIX = 'wai:b76:';

/**
 * B190 fix4 — deletion residue. Removes this account's id from EVERY
 * `wai:b76:*` once-only ledger on the device, found by PREFIX scan rather
 * than by naming `DUAL_ARRIVAL_PENDING_KEY` / `COACH_ACK_SEEN_KEY`
 * specifically, so a ledger this module adds later is covered without a
 * second edit at every call site. Best-effort, like every other helper here.
 */
export function forgetUser(userId: string | null | undefined): void {
  if (!userId || typeof window === 'undefined') return;
  let keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(LEDGER_KEY_PREFIX)) keys.push(key);
    }
  } catch {
    return;
  }
  for (const key of keys) {
    writeUserFlag(key, userId, false);
  }
}
